import { assert } from "@faro-lotv/foundation";

/**
 * Extended `File` class which allows us to set the `webkitRelativePath` to retain the folder structure.
 *
 * We do not extend the `File` class directly, because there are functions,
 * that would not be compatible with `FileExt`.
 * E.g. `URL.createObjectURL(fileExt)` must be written as
 * `URL.createObjectURL(fileExt.file)`
 *
 * This is needed because when using folder selection, we get the `webkitRelativePath`, but when using
 * drag & drop, the File objects don't have it set. And while browsers allow us to assign new values to
 * `webkitRelativePath`, the assignment has no effect. So we save it in a separate property.
 */

/**
 * Extended `File` class Implementation
 *
 * We want to send this with the webkitRelativePath to the worker, it is serialized
 * with structuredClone and deserialized in the worker.
 */
export const FileExt = class FileExtImpl {
  /**
   * Unfortunately, when FileExt is derived from File, structuredClone drops all extra properties
   * (e.g. webkitRelativePath) and the File object is not usable anymore.
   *
   * We use a real File object as it can be structureCloned and the ArrayBuffer that it uses is a
   * ([transferable object](https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Transferable_objects)).
   *
   * That means, the file survives survive serialization and deserialization.
   * The FileExt object has to be restored in the worker.
   *
   * This does not(!) work in the vitest environment with jsdom:
   * [Implement transferring support](https://github.com/jsdom/jsdom/issues/3287)
   */

  // webkitRelativePath needs to be public so it can be serialized

  isFaroFileExt = true;
  constructor(fileBits, fileName, options) {
    this.file = new File(fileBits, fileName, getOptions(options));
    // In browsers, webkitRelativePath is set to an empty string by default.
    this.webkitRelativePath = options?.webkitRelativePath ?? "";
    assert(!this.webkitRelativePath || this.webkitRelativePath === fileName || this.webkitRelativePath.endsWith(`/${fileName}`), `webkitRelativePath inconsistent with fileName: ${this.webkitRelativePath} !~ ${fileName}`);
  }

  /**
   * Re-Create the FileExt after it was serialized
   * @param file FileExt to be re-created
   * @param file.file The file to be re-created
   */
  static createFileExtFromSerialized(file) {
    if (isFileExt(file)) {
      const newFile = new FileExt([file.file], file.file.name, {
        ...(file.webkitRelativePath ? {
          webkitRelativePath: file.webkitRelativePath
        } : {}),
        ...(file.type ? {
          type: file.type
        } : {}),
        ...(file.lastModified ? {
          lastModified: file.lastModified
        } : {})
      });
      return newFile;
    }
    const newFile = new FileExt([file], file.name, {
      ...(file.webkitRelativePath ? {
        webkitRelativePath: file.webkitRelativePath
      } : {}),
      ...(file.type ? {
        type: file.type
      } : {}),
      ...(file.lastModified ? {
        lastModified: file.lastModified
      } : {})
    });
    return newFile;
  }

  /**
   * Creates a new FileExt from a File or FileExt with a new webkitRelativePath.
   * @param file The file to create the new FileExt from.
   * @param webkitRelativePath The new webkitRelativePath to set.
   * @returns A new FileExt with the specified webkitRelativePath.
   */
  static createFromFileWithNewPath(file, webkitRelativePath) {
    if (isFileExt(file)) {
      // If the file is already a FileExt, we can just set the new webkitRelativePath
      file.webkitRelativePath = webkitRelativePath;
      return file;
    }
    // If the file is a regular File, we need to create a new FileExt
    const newFile = new FileExt([file], file.name, {
      webkitRelativePath,
      ...(file.type ? {
        type: file.type
      } : {}),
      ...(file.lastModified ? {
        lastModified: file.lastModified
      } : {})
    });
    return newFile;
  }

  // Pass through the File API to the file property, except for the webkitRelativePath which is our own property.

  get name() {
    return this.file.name;
  }
  get lastModified() {
    return this.file.lastModified;
  }
  get type() {
    return this.file.type;
  }
  get size() {
    return this.file.size;
  }
  async arrayBuffer() {
    return this.file.arrayBuffer();
  }
  async text() {
    return this.file.text();
  }
  async bytes() {
    return this.file.bytes();
  }
  slice(start, end, contentType) {
    return this.file.slice(start, end, contentType);
  }
  stream() {
    return this.file.stream();
  }

  // Can make it easier to check the correct type of the object when debugging.
  get [Symbol.toStringTag]() {
    return "FileExt";
  }
};

/** @returns Adjusted options, compatible with `File` constructor. */
function getOptions(options) {
  if (!options) {
    return undefined;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- We want to remove `webkitRelativePath`.
  const {
    webkitRelativePath,
    ...rest
  } = options;
  return rest;
}

/** Type guard to check instance of FileExt, or a serialized FileExt (e.g. from postMessage()). */
export function isFileExt(file) {
  return file instanceof FileExt || !!(file.isFaroFileExt && file.file instanceof File);
}

/**
 * Represents either a native File object or a FileExt instance.
 * Use this type when a function or API can accept both standard File objects and the extended FileExt type.
 */

/**
 * @param file Native File, or FileExt.
 * @returns The underlying File, so it can be used with any API that expects a File.
 */
export function getRealFile(file) {
  if (isFileExt(file)) {
    return file.file;
  }
  return file;
}